import { useEffect, useRef, useState, useCallback } from "react";
import { useCoachDebugStore } from "../../stores/coachDebugStore";
import { actionRegistry } from "./registry";
import type { MachineState } from "./types";
import type {
  ChatMessage,
  JourneyPhase,
  WidgetDraftBuffer,
} from "../../generated-api";
import { client } from "../../lib/api";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

export type { MachineState };

const bootstrapQueryOptions = (overrideKind?: JourneyPhase["kind"]) => ({
  queryKey: ["coachBootstrap", overrideKind ?? "auto"],
  queryFn: () =>
    client.coach.getCoachBootstrap({ journeyPhaseOverrideKind: overrideKind }),
  // Only refetched explicitly (phase transitions), never on mount/focus
  staleTime: Infinity,
});

// Shared read of the server-resolved journey phase. Use its `kind` as a React
// `key` on the chat to reset the conversation when the phase changes.
export function useJourneyPhase(): JourneyPhase | null {
  const { data } = useQuery(bootstrapQueryOptions());
  return data?.journeyPhase ?? null;
}

export function useAssistantOrchestrator(options?: {
  journeyPhaseOverrideKind?: JourneyPhase["kind"] | null;
  conversationStarterOverride?: string | null;
}): {
  messages: ChatMessage[];
  machineState: MachineState;
  errorMessage: string | null;
  journeyPhase: JourneyPhase | null;
  suggestedPrompts: string[];
  send: (input: string) => Promise<void>;
  completeWidget: (kind: string, result: unknown) => Promise<void>;
} {
  const queryClient = useQueryClient();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [machineState, setMachineState] = useState<MachineState>("booting");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const widgetDraftsRef = useRef<WidgetDraftBuffer>({});
  const seededForRef = useRef<string | null>(null);
  const debugUpdate = useCoachDebugStore((s) => s.update);

  // 1. TanStack Query for Bootstrapping
  const {
    data: bootstrapData,
    error: bootstrapError,
    isError: isBootstrapError,
  } = useQuery(
    bootstrapQueryOptions(options?.journeyPhaseOverrideKind ?? undefined),
  );

  // The phase is always server-computed (automatic or overridden): read it
  // from the bootstrap query cache.
  const journeyPhase = bootstrapData?.journeyPhase ?? null;

  // Seed the conversation once per mount (and when overrides change). Later
  // cache updates must not wipe messages; a phase change remounts the chat.
  useEffect(() => {
    const seedKey = `${options?.journeyPhaseOverrideKind ?? "auto"}|${
      options?.conversationStarterOverride ?? ""
    }`;
    if (bootstrapData && seededForRef.current !== seedKey) {
      seededForRef.current = seedKey;
      widgetDraftsRef.current = bootstrapData.widgetDrafts || {};
      setMessages([
        {
          role: "assistant",
          content:
            options?.conversationStarterOverride ??
            bootstrapData.conversationStarter,
        },
      ]);
      setMachineState("awaiting_input");
    }
  }, [
    bootstrapData,
    options?.journeyPhaseOverrideKind,
    options?.conversationStarterOverride,
  ]);

  // Sync bootstrap error states
  useEffect(() => {
    if (isBootstrapError) {
      setErrorMessage(
        bootstrapError instanceof Error
          ? bootstrapError.message
          : "Bootstrap failed",
      );
      setMachineState("error");
    }
  }, [isBootstrapError, bootstrapError]);

  // 2. TanStack Mutation for responding to messages
  const respondMutation = useMutation({
    mutationFn: async (payload: {
      nextMessages: ChatMessage[];
      explicitDrafts: WidgetDraftBuffer;
    }) => {
      const requestBody = {
        messages: payload.nextMessages,
        widgetDrafts: payload.explicitDrafts,
        journeyPhaseOverrideKind:
          options?.journeyPhaseOverrideKind ?? undefined,
      };

      return client.coach.postCoachRespond({
        requestBody,
      });
    },
    // General mutation-complete signifier (see main.tsx's MutationCache):
    // surfaces as a toast via the same opt-in mechanism any mutation can use.
    meta: { successMessage: "Your coach replied." },
    onSuccess: async (data) => {
      widgetDraftsRef.current = data.widgetDrafts || {};

      // Append backend response to message array
      setMessages((prev) => [...prev, ...data.messages]);
      setMachineState("awaiting_input");

      if (options?.journeyPhaseOverrideKind) return;
      if (data.journeyPhase.kind !== bootstrapData?.journeyPhase.kind) {
        // Phase transition: refetch bootstrap so phase, starter message and
        // suggested prompts change together (consumers remount on new kind).
        await queryClient.invalidateQueries({ queryKey: ["coachBootstrap"] });
      } else {
        queryClient.setQueryData(
          bootstrapQueryOptions().queryKey,
          (old: typeof bootstrapData) =>
            old && { ...old, journeyPhase: data.journeyPhase },
        );
      }
    },
    onError: (err) => {
      setErrorMessage(
        err instanceof Error ? err.message : "Response generation failed",
      );
      setMachineState("error");
    },
  });

  // 3. Core pipeline wrapper to trigger mutation execution
  const executeSend = useCallback(
    async (input: string, explicitDrafts: WidgetDraftBuffer) => {
      const nextMessages: ChatMessage[] = [
        ...messages,
        { role: "user" as const, content: input },
      ];

      // Optimistically push the chat UI update
      setMessages(nextMessages);
      setMachineState("awaiting_llm");

      // Fire TanStack mutation
      respondMutation.mutate({ nextMessages, explicitDrafts });
    },
    [messages, respondMutation],
  );

  // 4. Exposed send message function
  const send = useCallback(
    async (input: string) => {
      if (machineState !== "awaiting_input") {
        return;
      }
      await executeSend(input, widgetDraftsRef.current);
    },
    [machineState, executeSend],
  );

  // 5. Widget action executor and state cleaner
  const completeWidget = useCallback(
    async (kind: string, result: unknown) => {
      if (
        machineState === "running_actions" ||
        machineState === "awaiting_llm"
      ) {
        return;
      }

      const entry = actionRegistry[kind];
      const draft = widgetDraftsRef.current[kind as keyof WidgetDraftBuffer];

      if (entry?.action) {
        setMachineState("running_actions");
        try {
          await entry.action(draft);
        } catch (err) {
          setErrorMessage(
            err instanceof Error ? err.message : "Action execution failed",
          );
          setMachineState("error");
          return;
        }
      }

      // Invalidate after the action has mutated server state. Bootstrap is
      // excluded: the respond round below returns the fresh phase and
      // triggers the bootstrap refetch (avoids remounting mid-flow).
      await queryClient.invalidateQueries({
        predicate: (q) => q.queryKey[0] !== "coachBootstrap",
      });

      const updatedDrafts = { ...widgetDraftsRef.current };
      delete updatedDrafts[kind as keyof WidgetDraftBuffer];

      widgetDraftsRef.current = updatedDrafts;

      await executeSend(`[${kind}: ${JSON.stringify(result)}]`, updatedDrafts);
    },
    [machineState, executeSend, queryClient],
  );

  // 6. DevTools state synchronization
  useEffect(() => {
    if (import.meta.env.DEV) {
      debugUpdate({
        machineState,
        messages,
        errorMessage,
        widgetDrafts: widgetDraftsRef.current,
        journeyPhase,
      });
    }
  }, [machineState, messages, errorMessage, journeyPhase, debugUpdate]);

  return {
    messages,
    machineState,
    errorMessage,
    journeyPhase,
    suggestedPrompts: bootstrapData?.suggestedPrompts ?? [],
    send,
    completeWidget,
  };
}

export const useAssistant = useAssistantOrchestrator;
